import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, Upload, Send, Image, Sparkles, Star } from 'lucide-react';
import { toast } from 'sonner';
import AuthModal from '@/components/AuthModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubmitPrompt() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [platformId, setPlatformId] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [categories, setCategories] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Shoutout state
  const [shoutoutSearch, setShoutoutSearch] = useState('');
  const [shoutoutResults, setShoutoutResults] = useState([]);
  const [shoutoutSelected, setShoutoutSelected] = useState(null);
  const [karma, setKarma] = useState(null);

  // Load categories & platforms (public, no auth needed)
  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [catRes, platRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/platforms`),
        ]);
        setCategories(catRes.data);
        setPlatforms(platRes.data);
      } catch {}
    };
    loadMeta();
  }, []);

  // Load karma only when user is logged in
  useEffect(() => {
    if (user?.id) {
      axios.get(`${API}/users/${user.id}/karma`).then(r => setKarma(r.data.karma)).catch(() => {});
    }
  }, [user]);

  // Search prompts for shoutout
  useEffect(() => {
    if (shoutoutSearch.length < 2) { setShoutoutResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/prompts/search-titles?q=${encodeURIComponent(shoutoutSearch)}`);
        setShoutoutResults(res.data);
      } catch { setShoutoutResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [shoutoutSearch]);

  const handleScreenshot = (e) => {
    const files = Array.from(e.target.files);
    if (screenshots.length + files.length > 5) { toast.error('Max 5 screenshots'); return; }
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} exceeds 5MB`); return; }
      const reader = new FileReader();
      reader.onload = (ev) => setScreenshots(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(file);
    });
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  const toggleCategory = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) { toast.error('Title and content required'); return; }
    if (!user) { setShowAuthModal(true); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/prompts`, {
        title, content,
        categories: selectedCategories,
        platform_id: platformId,
        screenshots,
        shoutout_prompt_id: shoutoutSelected?.id || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Prompt shared!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="submit-prompt-page">
      <Card className="border-border/50 shadow-xl">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Share a Prompt</CardTitle>
          <CardDescription>Help the community by sharing your best AI prompts</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="submit-prompt-form">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input id="title" placeholder="e.g. Generate a Marketing Strategy" value={title} onChange={(e) => setTitle(e.target.value)} required className="h-11" data-testid="prompt-title-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Prompt Content</Label>
              <Textarea id="content" placeholder="Write your prompt here. Use [brackets] for variables..." value={content} onChange={(e) => setContent(e.target.value)} required className="min-h-[160px] font-mono text-sm" data-testid="prompt-content-input" />
            </div>

            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={platformId} onValueChange={setPlatformId}>
                <SelectTrigger className="h-11" data-testid="prompt-platform-select">
                  <SelectValue placeholder="Select AI platform" />
                </SelectTrigger>
                <SelectContent>
                  {platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Categories</Label>
              <div className="flex flex-wrap gap-2" data-testid="category-selector">
                {categories.map(cat => (
                  <Badge
                    key={cat.id}
                    variant={selectedCategories.includes(cat.name) ? 'default' : 'outline'}
                    className={`cursor-pointer rounded-full transition-all ${selectedCategories.includes(cat.name) ? 'shadow-lg shadow-primary/20' : 'hover:bg-accent'}`}
                    onClick={() => toggleCategory(cat.name)}
                    data-testid={`cat-badge-${cat.slug}`}
                  >
                    {cat.name}
                    {selectedCategories.includes(cat.name) && <X className="w-3 h-3 ml-1" />}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Screenshots (optional, max 5)</Label>
              <div className="flex flex-wrap gap-3">
                {screenshots.map((src, i) => (
                  <div key={i} className="relative group w-24 h-24 rounded-lg overflow-hidden border border-border">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeScreenshot(i)}
                      className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-screenshot-${i}`}
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>
                ))}
                {screenshots.length < 5 && (
                  <label className="w-24 h-24 rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center cursor-pointer transition-colors text-muted-foreground hover:text-primary" data-testid="upload-screenshot-btn">
                    <Image className="w-5 h-5 mb-1" />
                    <span className="text-xs">Upload</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handleScreenshot} />
                  </label>
                )}
              </div>
            </div>

            {/* Shoutout Picker */}
            <div className="space-y-2" data-testid="shoutout-section">
              <Label className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" /> Shoutout
                {karma !== null && karma < 20 && (
                  <span className="text-xs text-destructive font-normal">(Required until 20 karma — you have {karma})</span>
                )}
                {karma !== null && karma >= 20 && (
                  <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
                )}
              </Label>
              {shoutoutSelected ? (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20">
                  <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{shoutoutSelected.title}</p>
                    <p className="text-xs text-muted-foreground">by {shoutoutSelected.username}</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setShoutoutSelected(null); setShoutoutSearch(''); }} data-testid="remove-shoutout-btn">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Input
                    placeholder="Search for a prompt to shoutout..."
                    value={shoutoutSearch}
                    onChange={(e) => setShoutoutSearch(e.target.value)}
                    className="h-11"
                    data-testid="shoutout-search-input"
                  />
                  {shoutoutResults.length > 0 && (
                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="shoutout-results">
                      {shoutoutResults.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-accent transition-colors text-sm"
                          onClick={() => { setShoutoutSelected(p); setShoutoutSearch(''); setShoutoutResults([]); }}
                          data-testid={`shoutout-option-${p.id}`}
                        >
                          <span className="font-medium">{p.title}</span>
                          <span className="text-xs text-muted-foreground ml-2">by {p.username}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button type="submit" className="w-full h-11 rounded-full shadow-lg shadow-primary/20 gap-2" disabled={loading} data-testid="submit-prompt-btn">
              {loading ? 'Sharing...' : 'Share Prompt'} <Send className="w-4 h-4" />
            </Button>
          </form>
        </CardContent>
      </Card>
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} hint="Sign in to share your prompt with the community." />
    </div>
  );
}
